"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import type { WordActionAnchor } from "./WordActionBar";

interface SelectableTextProps {
  children: React.ReactNode;
  onWordClick: (word: string, position: WordActionAnchor) => void;
  targetLanguage: string;
  languageCode?: string;
  className?: string;
  enableMessageAction?: boolean;
}

interface WordBounds {
  word: string;
  textNode: Text;
  start: number;
  end: number;
}

const WORD_HOVER_HIGHLIGHT = "selectable-text-word-hover";
const HOVER_STYLE_ID = "selectable-text-hover-styles";
const SELECTABLE_CONTENT_SELECTOR = '[data-word-selectable-content="true"]';
const WORD_BOUNDARY_CHAR_PATTERN = /[\w\u0080-\uFFFF'-]/;

function useHoverCapable(): boolean {
  const [canHover, setCanHover] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(hover: hover)");
    const update = () => setCanHover(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return canHover;
}

function supportsCssHighlights(): boolean {
  return typeof CSS !== "undefined" && "highlights" in CSS && typeof Highlight !== "undefined";
}

function isValidSelectableText(text: string): boolean {
  const normalized = text.trim().replace(/\s+/g, " ");
  return normalized.length >= 2 && /[a-zA-Z\u0080-\uFFFF]/.test(normalized);
}

function isValidSelectableWord(word: string): boolean {
  return isValidSelectableText(word);
}

function normalizeSelectedText(text: string): string {
  return text.trim().replace(/\s+/g, " ");
}

function isWordBoundaryChar(char: string | undefined): boolean {
  return !!char && WORD_BOUNDARY_CHAR_PATTERN.test(char);
}

function isSelectionWithin(container: HTMLElement, selection: Selection): boolean {
  if (selection.rangeCount === 0) {
    return false;
  }

  const range = selection.getRangeAt(0);
  if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
    return false;
  }

  if (!container.querySelector(SELECTABLE_CONTENT_SELECTOR)) {
    return true;
  }

  return (
    isNodeInSelectableContent(container, range.startContainer) &&
    isNodeInSelectableContent(container, range.endContainer)
  );
}

function getNodeElement(node: Node): Element | null {
  return node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
}

function isNodeInSelectableContent(container: HTMLElement, node: Node): boolean {
  const selectableContent = getNodeElement(node)?.closest(SELECTABLE_CONTENT_SELECTOR);
  return !!selectableContent && container.contains(selectableContent);
}

function getRangeAnchor(range: Range, fallback: { x: number; y: number }): WordActionAnchor {
  const rects = Array.from(range.getClientRects()).filter(
    (rect) => rect.width > 0 && rect.height > 0
  );

  if (rects.length === 0) {
    return fallback;
  }

  const left = Math.min(...rects.map((rect) => rect.left));
  const right = Math.max(...rects.map((rect) => rect.right));
  const top = Math.min(...rects.map((rect) => rect.top));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));

  return {
    x: left + (right - left) / 2,
    y: top,
    top,
    bottom,
  };
}

function getSelectionPosition(selection: Selection): WordActionAnchor {
  const range = selection.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  return getRangeAnchor(range, {
    x: rect.left + rect.width / 2,
    y: rect.top,
  });
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target.tagName === "BUTTON" ||
    target.tagName === "A" ||
    target.tagName === "INPUT" ||
    !!target.closest("button, a, input")
  );
}

function getCaretRange(x: number, y: number): Range | null {
  if (typeof document.caretRangeFromPoint === "function") {
    return document.caretRangeFromPoint(x, y);
  }

  const caretPos = (
    document as Document & {
      caretPositionFromPoint?: (
        x: number,
        y: number
      ) => { offsetNode: Node; offset: number } | null;
    }
  ).caretPositionFromPoint?.(x, y);

  if (caretPos) {
    const range = document.createRange();
    range.setStart(caretPos.offsetNode, caretPos.offset);
    range.collapse(true);
    return range;
  }

  return null;
}

function expandTextRangeToWordBoundaries(range: Range): Range {
  const expandedRange = range.cloneRange();

  if (expandedRange.startContainer.nodeType === Node.TEXT_NODE) {
    const textNode = expandedRange.startContainer as Text;
    const text = textNode.textContent || "";
    let start = expandedRange.startOffset;

    while (start > 0 && isWordBoundaryChar(text[start - 1])) {
      start--;
    }

    expandedRange.setStart(textNode, start);
  }

  if (expandedRange.endContainer.nodeType === Node.TEXT_NODE) {
    const textNode = expandedRange.endContainer as Text;
    const text = textNode.textContent || "";
    let end = expandedRange.endOffset;

    while (end < text.length && isWordBoundaryChar(text[end])) {
      end++;
    }

    expandedRange.setEnd(textNode, end);
  }

  return expandedRange;
}

function getWordBoundsAtPoint(
  clickX: number,
  clickY: number,
  languageCode?: string,
  container?: HTMLElement | null
): WordBounds | null {
  const range = getCaretRange(clickX, clickY);
  if (!range) return null;

  const clickedNode = range.startContainer;
  if (container?.querySelector(SELECTABLE_CONTENT_SELECTOR)) {
    if (!isNodeInSelectableContent(container, clickedNode)) {
      return null;
    }
  }

  if (clickedNode.nodeType !== Node.TEXT_NODE) {
    return null;
  }

  const textNode = clickedNode as Text;
  const nodeText = textNode.textContent || "";
  if (!nodeText.trim()) {
    return null;
  }

  const clickOffset = range.startOffset;
  if (clickOffset < 0 || clickOffset > nodeText.length) {
    return null;
  }

  if (languageCode && typeof Intl !== "undefined" && "Segmenter" in Intl) {
    try {
      let segmenterLang = languageCode;
      if (languageCode.startsWith("yue")) segmenterLang = "zh-HK";
      if (languageCode.startsWith("cmn")) segmenterLang = "zh-CN";

      const segmenter = new Intl.Segmenter(segmenterLang, { granularity: "word" });
      for (const segment of segmenter.segment(nodeText)) {
        if (clickOffset >= segment.index && clickOffset < segment.index + segment.segment.length) {
          const word = segment.isWordLike
            ? segment.segment
            : segment.segment.trim() && /[\w\u0080-\uFFFF]/.test(segment.segment)
              ? segment.segment
              : null;

          if (word) {
            return {
              word,
              textNode,
              start: segment.index,
              end: segment.index + segment.segment.length,
            };
          }
        }
      }
    } catch {
      // Fall back to regex word detection below.
    }
  }

  let wordStart = clickOffset;
  while (wordStart > 0 && isWordBoundaryChar(nodeText[wordStart - 1])) {
    wordStart--;
  }

  let wordEnd = clickOffset;
  while (wordEnd < nodeText.length && isWordBoundaryChar(nodeText[wordEnd])) {
    wordEnd++;
  }

  const word = nodeText
    .slice(wordStart, wordEnd)
    .trim()
    .replace(/^['-]+|['-]+$/g, "");
  if (!word) return null;

  return { word, textNode, start: wordStart, end: wordEnd };
}

function getWordRange(bounds: WordBounds): Range {
  const range = document.createRange();
  range.setStart(bounds.textNode, bounds.start);
  range.setEnd(bounds.textNode, bounds.end);
  return range;
}

function getRangeUnderlineRects(range: Range): DOMRect[] {
  return Array.from(range.getClientRects()).filter((rect) => rect.width > 0 && rect.height > 0);
}

function clearWordHoverHighlight(): void {
  if (supportsCssHighlights()) {
    CSS.highlights.delete(WORD_HOVER_HIGHLIGHT);
  }
}

function setWordHoverHighlight(range: Range): void {
  if (!supportsCssHighlights()) return;
  CSS.highlights.set(WORD_HOVER_HIGHLIGHT, new Highlight(range));
}

export function SelectableText({
  children,
  onWordClick,
  targetLanguage: _targetLanguage,
  languageCode,
  className = "",
  enableMessageAction = true,
}: SelectableTextProps) {
  const contentId = React.useId();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const hoverFrameRef = React.useRef<number | null>(null);
  const pointerStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const selectionDebounceRef = React.useRef<number | null>(null);
  const canHover = useHoverCapable();
  const useNativeHighlight = React.useMemo(() => supportsCssHighlights(), []);
  const [underlineRects, setUnderlineRects] = React.useState<DOMRect[]>([]);
  const [portalRoot, setPortalRoot] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    setPortalRoot(document.body);
  }, []);

  React.useEffect(() => {
    if (!useNativeHighlight) return;

    if (document.getElementById(HOVER_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = HOVER_STYLE_ID;
    style.textContent = `
      ::highlight(${WORD_HOVER_HIGHLIGHT}) {
        text-decoration: underline;
        text-decoration-color: #fd954f;
        text-decoration-thickness: 2px;
        text-underline-offset: 3px;
      }
    `;
    document.head.appendChild(style);
  }, [useNativeHighlight]);

  React.useEffect(() => {
    return () => {
      clearWordHoverHighlight();
    };
  }, []);

  const detectWord = React.useCallback(
    (clickX: number, clickY: number): { word: string; position: WordActionAnchor } | null => {
      const bounds = getWordBoundsAtPoint(clickX, clickY, languageCode, containerRef.current);
      if (!bounds || !isValidSelectableWord(bounds.word)) {
        return null;
      }
      return {
        word: bounds.word,
        position: getRangeAnchor(getWordRange(bounds), { x: clickX, y: clickY }),
      };
    },
    [languageCode]
  );

  const emitSelection = React.useCallback(
    (text: string, position: WordActionAnchor) => {
      onWordClick(text, position);
    },
    [onWordClick]
  );

  const tryEmitTextSelection = React.useCallback((): boolean => {
    const container = containerRef.current;
    const selection = window.getSelection();
    if (!container || !selection || selection.isCollapsed) {
      return false;
    }

    if (!isSelectionWithin(container, selection)) {
      return false;
    }

    const roundedRange = expandTextRangeToWordBoundaries(selection.getRangeAt(0));
    const selectedText = normalizeSelectedText(roundedRange.toString());
    if (!isValidSelectableText(selectedText)) {
      return false;
    }

    emitSelection(selectedText, getRangeAnchor(roundedRange, getSelectionPosition(selection)));
    return true;
  }, [emitSelection]);

  const clearHover = React.useCallback(() => {
    clearWordHoverHighlight();
    setUnderlineRects([]);
  }, []);

  const updateHoverUnderline = React.useCallback(
    (clientX: number, clientY: number) => {
      if (!canHover) return;

      const bounds = getWordBoundsAtPoint(clientX, clientY, languageCode, containerRef.current);
      if (!bounds || !isValidSelectableWord(bounds.word)) {
        clearHover();
        return;
      }

      const range = getWordRange(bounds);

      if (useNativeHighlight) {
        setWordHoverHighlight(range);
        setUnderlineRects([]);
        return;
      }

      setUnderlineRects(getRangeUnderlineRects(range));
    },
    [canHover, clearHover, languageCode, useNativeHighlight]
  );

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canHover) return;

    if (hoverFrameRef.current !== null) {
      cancelAnimationFrame(hoverFrameRef.current);
    }

    const { clientX, clientY } = event;
    hoverFrameRef.current = requestAnimationFrame(() => {
      updateHoverUnderline(clientX, clientY);
      hoverFrameRef.current = null;
    });
  };

  const handleMouseLeave = () => {
    if (hoverFrameRef.current !== null) {
      cancelAnimationFrame(hoverFrameRef.current);
      hoverFrameRef.current = null;
    }
    clearHover();
  };

  React.useEffect(() => {
    return () => {
      if (hoverFrameRef.current !== null) {
        cancelAnimationFrame(hoverFrameRef.current);
      }
      if (selectionDebounceRef.current !== null) {
        window.clearTimeout(selectionDebounceRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const handleSelectionChange = () => {
      if (selectionDebounceRef.current !== null) {
        window.clearTimeout(selectionDebounceRef.current);
      }

      selectionDebounceRef.current = window.setTimeout(() => {
        tryEmitTextSelection();
        selectionDebounceRef.current = null;
      }, 160);
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      if (selectionDebounceRef.current !== null) {
        window.clearTimeout(selectionDebounceRef.current);
      }
    };
  }, [tryEmitTextSelection]);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    clearHover();
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isInteractiveTarget(event.target)) {
      return;
    }

    if (tryEmitTextSelection()) {
      pointerStartRef.current = null;
      return;
    }

    const pointerStart = pointerStartRef.current;
    pointerStartRef.current = null;
    const moved =
      pointerStart !== null &&
      (Math.abs(event.clientX - pointerStart.x) > 4 ||
        Math.abs(event.clientY - pointerStart.y) > 4);

    if (moved) {
      if (selectionDebounceRef.current !== null) {
        window.clearTimeout(selectionDebounceRef.current);
      }
      selectionDebounceRef.current = window.setTimeout(() => {
        tryEmitTextSelection();
        selectionDebounceRef.current = null;
      }, 0);
      return;
    }

    if (selectionDebounceRef.current !== null) {
      window.clearTimeout(selectionDebounceRef.current);
    }
    selectionDebounceRef.current = window.setTimeout(() => {
      tryEmitTextSelection();
      selectionDebounceRef.current = null;
    }, 0);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isInteractiveTarget(event.target)) {
      return;
    }

    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      return;
    }

    const wordSelection = detectWord(event.clientX, event.clientY);
    if (wordSelection) {
      emitSelection(wordSelection.word, wordSelection.position);
    }
  };

  const fallbackUnderlines =
    canHover && !useNativeHighlight && underlineRects.length > 0 && portalRoot
      ? createPortal(
          <>
            {underlineRects.map((rect, index) => (
              <div
                key={`${rect.left}-${rect.top}-${index}`}
                aria-hidden
                className="pointer-events-none fixed rounded-full bg-[#FD954F]"
                style={{
                  left: `${rect.left}px`,
                  top: `${rect.bottom - 1}px`,
                  width: `${rect.width}px`,
                  height: "2px",
                  zIndex: 9999,
                }}
              />
            ))}
          </>,
          portalRoot
        )
      : null;

  return (
    <>
      <div
        id={contentId}
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        onMouseMove={canHover ? handleMouseMove : undefined}
        onMouseLeave={canHover ? handleMouseLeave : undefined}
        className={`cursor-pointer select-text ${className}`}
      >
        {children}
      </div>
      {enableMessageAction && (
        <button
          type="button"
          aria-describedby={contentId}
          className="sr-only focus:not-sr-only focus:my-2 focus:min-h-11 focus:rounded-lg focus:border focus:border-white/20 focus:px-3 focus:text-sm focus:text-white"
          onClick={(event) => {
            const container = containerRef.current;
            if (!container) return;
            const parts = container.querySelectorAll(SELECTABLE_CONTENT_SELECTOR);
            const text = normalizeSelectedText(
              parts.length
                ? Array.from(parts)
                    .map((part) => part.textContent || "")
                    .join(" ")
                : container.textContent || ""
            );
            if (!isValidSelectableText(text)) return;
            const rect = event.currentTarget.getBoundingClientRect();
            emitSelection(text, {
              x: rect.left + rect.width / 2,
              y: rect.top,
              top: rect.top,
              bottom: rect.bottom,
            });
          }}
        >
          Create flashcard from this message
        </button>
      )}
      {fallbackUnderlines}
    </>
  );
}
